import { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Play, ChevronUp, ChevronDown, Users, Plus, Calendar, Eye, EyeOff, ScanLine, Monitor, Clock, AlertTriangle, Printer, FileDown, Trophy, Sparkles, SlidersHorizontal, ListOrdered, Scissors } from 'lucide-react';
import { sanitizeHtml } from '../../utils/sanitize';
import { getQuestionTypeLabel, estQcmSimple } from '../../utils/questionUtils';
import { openEvalPrintPreview, openGroupesPrint } from '../../utils/evalPapier';
import { construireCartesAide } from '../../utils/plancheAides';
import EquipeForm from './EquipeForm';
import QuestionPreviewModal from './QuestionPreviewModal';
import { pluriel } from './formatConfig';

// Identité d'un item de session : id DB pour les questions de la banque,
// _tmpId (étiquette client posée par generatedItems) pour les exercices
// générés pas encore persistés — ils n'obtiendront leur vrai id qu'au
// lancement, via banque-questions/bulk-create.
const keyOf = (q) => q.id ?? q._tmpId;

// Formats de passation connus — hoisté (identités stables).
const FORMATS_DEFAUT = ['aruco', 'en_ligne', 'papier'];

// Mots du bloc « Donner aussi à » selon ce que l'on compose : une classe, un
// demi-groupe ou un groupe de besoin (2026-09-30).
const EQUIPES_GROUPE = 'Les groupes supplémentaires jouent en individuel : les équipes sont propres à un groupe.';
const MOTS_CIBLES = {
  classe: {
    titre: 'Autres classes du même niveau',
    chaque: 'chaque classe',
    equipes: 'Les classes supplémentaires jouent en individuel : les équipes sont propres à une classe.',
  },
  demiGroupe: { titre: 'Autres demi-groupes du même niveau', chaque: 'chaque groupe', equipes: EQUIPES_GROUPE },
  groupeDeBesoin: { titre: 'Autres groupes de besoin du même niveau', chaque: 'chaque groupe', equipes: EQUIPES_GROUPE },
};

// Pli replié : titre + résumé des valeurs courantes ; le contenu n'est monté
// qu'ouvert. Composant statique, hors du modal (jamais recréé au rendu).
/* Pli = carte ouvrable (2026-09-03) : icône, titre en gras, l'état courant en
   pastilles quand elle est fermée, chevron à droite. Fermée, elle dit déjà
   tout ; rien de plus n'est ajouté pour le prof pressé, c'est le même
   contenu mis en forme. */
const Pli = ({ titre, icone, badges = [], ouvert, onToggle, children }) => (
  <div className={`rounded-xl border ${ouvert ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50/70'}`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={ouvert}
      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-gray-100/70 rounded-xl transition"
    >
      {icone ? <span className="text-gray-500 flex-shrink-0">{icone}</span> : null}
      <span className="text-sm font-semibold text-gray-800 flex-shrink-0">{titre}</span>
      {!ouvert && badges.length > 0 ? (
        <span className="flex flex-wrap gap-1 min-w-0">
          {badges.map((b) => (
            <span key={b} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-600 whitespace-nowrap">
              {b}
            </span>
          ))}
        </span>
      ) : null}
      <ChevronDown size={16} className={`ml-auto text-gray-500 flex-shrink-0 transition-transform ${ouvert ? '' : '-rotate-90'}`} />
    </button>
    {ouvert ? <div className="px-3.5 pb-3.5 space-y-4">{children}</div> : null}
  </div>
);
// withTitre : affiche un champ Titre dans le volet session et l'inclut dans la
// config onLaunch — utilisé par EvalBilan (les bilans EvalNat ont un titre).
// titreAuto : les séances QCM (en ligne, cartes), fonction (enLigne) → titre
// automatique ; le champ Titre vit alors dans « Réglages », pré-rempli de ce
// titre, et un titre vidé le reprend (demande du prof, 2026-10-04).
// initialConfig : valeurs initiales (édition d'un bilan existant) — lues UNE
// FOIS au montage, le modal étant remonté à chaque ouverture.
const LaunchModal = ({ launchMode: passationMode = 'aruco', selectedQuestions, adaptation = null, autresClasses = [], typeCibles = 'classe', avancementClasses = null, availableFormats: formatsProposes = FORMATS_DEFAUT, motifCartes = null, sujets = null, coupsDePouceParSeance = false, printContext = null, withTitre = false, titreAuto = null, initialConfig = null, cartes, eleves, allElevesAssigned = true, isLoading, error, onLaunch, onClose, onGoToCartes }) => {
  // Format de distribution — pré-positionné par le bouton cliqué dans la
  // barre, changeable sans fermer (le modal est monté/démonté à chaque
  // ouverture, la prop est donc figée par ouverture : état initial légitime).
  // Le format initial doit appartenir aux formats DISPONIBLES : avec une
  // restriction (ex. classe vide → papier seul), un passationMode hors liste
  // bloquerait le modal sur un panneau inaccessible, sélecteur masqué.
  // Plus de sélecteur de format dans la modale (allègement 2026-09-02) : le
  // bouton cliqué dans la barre a déjà choisi ; changer d'avis, c'est fermer
  // et cliquer l'autre bouton, juste en dessous. Dérivé, pas d'état.
  // R17 (chantier composer-evaluation) : `motifCartes` dit pourquoi les
  // cartes QR sont indisponibles (« Cartes QR : 1 exercice sans version
  // QCM ») ; le format est alors retiré, même demandé, et rien n'est laissé
  // de côté en silence.
  const availableFormats = motifCartes ? formatsProposes.filter((f) => f !== 'aruco') : formatsProposes;
  const format = availableFormats.includes(passationMode) ? passationMode : availableFormats[0];
  // Plis : l'essentiel visible (adaptation en une ligne, Quand), les réglages
  // et l'ordre des questions repliés, leurs valeurs courantes lues dans le
  // titre du pli — huit zones d'un coup, c'était trop pour deux minutes
  // entre deux cours.
  const [reglagesOuverts, setReglagesOuverts] = useState(false);
  const [ordreOuvert, setOrdreOuvert] = useState(false);
  const [adaptationDepliee, setAdaptationDepliee] = useState(false);
  // « Donner aussi à » : classes du même niveau cochées, et l'option
  // « nombres différents » (décochée : mêmes questions partagées).
  const [classesSupp, setClassesSupp] = useState(() => new Set());
  const [nombresDifferents, setNombresDifferents] = useState(false);
  const basculerClasse = useCallback((id) => {
    setClassesSupp((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const motsCibles = MOTS_CIBLES[typeCibles] || MOTS_CIBLES.classe;
  const isEnLigne = format === 'en_ligne';
  const isAruco = format === 'aruco';
  const isPapier = format === 'papier';
  const [mode, setMode] = useState('individuel');
  const [dureeQuiz, setDureeQuiz] = useState(initialConfig?.dureeQuiz ?? 30); // Durée globale en minutes (mode en ligne)
  const [orderedQuestions, setOrderedQuestions] = useState(selectedQuestions);
  const [equipes, setEquipes] = useState([]);
  const [showEquipeForm, setShowEquipeForm] = useState(true);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [launchMode, setLaunchMode] = useState(initialConfig?.datePassation ? 'schedule' : 'now');
  const [datePassation, setDatePassation] = useState(initialConfig?.datePassation || '');
  const [correctionVisible, setCorrectionVisible] = useState(initialConfig?.correctionVisible ?? false);
  // R16 : coups de pouce de la séance en ligne, non par défaut, seulement si
  // le parent les transmet (pas les bilans EvalNat). En groupes, chaque
  // groupe a le sien : pas de case commune.
  const [coupsDePouce, setCoupsDePouce] = useState(false);
  const caseCoupsDePouce = isEnLigne && coupsDePouceParSeance;
  // Déroulé de fin de séance projetée — passation cartes uniquement (D1).
  // Défauts alignés sur ceux du modèle (`SessionQCM.podium_actif` /
  // `mode_correction`, phase 0) : le formulaire ne fait que rendre visibles
  // les valeurs que le serveur poserait de lui-même.
  const [podiumActif, setPodiumActif] = useState(true);
  const [modeCorrection, setModeCorrection] = useState('finale');
  // Titre de session/bilan — bilans (withTitre) : champ requis. Séances QCM
  // (titreAuto) : le titre automatique, figé à l'ouverture, pré-remplit le champ.
  const [titreAutomatique] = useState(() => (titreAuto ? titreAuto(isEnLigne) : ''));
  const [titreSession, setTitreSession] = useState(initialConfig?.titre || titreAutomatique);

  // ─── Format "Éval papier" ───────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const [printMeta, setPrintMeta] = useState(() => {
    // Pré-remplissage vitesse : titre horodaté (modifiable) + classe auto
    // depuis le contexte — le prof peut ouvrir l'aperçu sans rien saisir.
    const maintenant = new Date();
    const titreDefaut = `Éval Formative du ${maintenant.toLocaleDateString('fr-FR')} à ${maintenant.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    return {
      titre: titreDefaut,
      classe: printContext?.nomClasse || '',
      // Durée et barème retirés du formulaire (2026-09-02) : ils ne servaient
      // qu'à deux pastilles de l'en-tête imprimé, absentes quand null.
      duree: null,
      bareme: null,
      corrigesMode: 'auto', // fixe — le sélecteur a été retiré du formulaire
      date: today,
      documentType: 'evaluation', // fixe — le sélecteur Évaluation/Entraînement a été retiré (2026-09-03)
      saisieMode: 'sur_la_feuille', // 'sur_la_feuille' | 'sur_le_cahier'
      // Planche de coups de pouce à découper : DÉCOCHÉE par défaut. C'est un
      // choix, jamais un défaut — on n'aide pas un élève pendant un contrôle,
      // la planche sert d'abord en entraînement et en remédiation.
      coupsDePouce: false,
    };
  });
  // Cartes d'aide réellement disponibles pour ces questions. MÊME fonction que
  // celle qui construit la planche imprimée : le compte annoncé ici ne peut pas
  // diverger de ce que le prof obtiendra. Toutes les fiches ne s'impriment pas
  // (figures animées à l'écran), d'où un compte et non un simple booléen.
  const cartesAide = useMemo(
    () => construireCartesAide(orderedQuestions.map((q) => ({ payload: q }))),
    [orderedQuestions]
  );
  const nbExosAides = useMemo(
    () => new Set(cartesAide.flatMap((c) => c.blocs)).size,
    [cartesAide]
  );

  // Groupes de besoin (F7 papier) : un seul document, dans l'ordre de la
  // distribution — récapitulatif, sujets sous un code neutre (« Sujet 1 »)
  // avec leur planche de coups de pouce, puis les corrigés (2026-10-04).
  // Planche d'un sujet : autorisée pour son groupe et au moins une carte imprimable.
  const avecPlanche = (sujet) => sujet.plancheAides && construireCartesAide(sujet.exercices.map((q) => ({ payload: q }))).length > 0;
  const ouvrirGroupes = () => {
    const ok = openGroupesPrint({
      meta: printMeta,
      classe: printMeta.classe,
      sujets: sujets.map((s) => ({ titre: s.titre, eleves: s.eleves, exercices: s.exercices, coupsDePouce: avecPlanche(s) })),
    });
    // eslint-disable-next-line no-alert
    if (!ok) alert('Impossible de préparer le document (stockage local indisponible).');
  };

  const handleOpenPrint = () => {
    // Le print respecte l'ordre choisi dans le modal. Le contexte
    // (classe/établissement/matière) part avec : il active le panneau
    // "Évaluation de suivi" dans l'aperçu (création POST /evaluations/ là-bas,
    // une fois la numérotation finale connue). Plus de type « entraînement »
    // depuis le retrait du sélecteur (2026-09-03).
    // Défensif : jamais l'option à vrai sans matière à imprimer.
    const meta = { ...printMeta, coupsDePouce: printMeta.coupsDePouce && cartesAide.length > 0 };
    const ok = openEvalPrintPreview(orderedQuestions, meta, printContext);
    if (!ok) {
      // eslint-disable-next-line no-alert
      alert('Impossible de préparer l\'aperçu PDF (stockage local indisponible).');
    }
  };

  const elevesDejaAssignes = useMemo(() => {
    const ids = new Set();
    equipes.forEach((eq) => eq.eleves.forEach((id) => ids.add(id)));
    return ids;
  }, [equipes]);

  const elevesDisponibles = useMemo(
    () => eleves.filter((e) => !elevesDejaAssignes.has(e.id)),
    [eleves, elevesDejaAssignes]
  );

  const cartesDejaAttribuees = useMemo(() => {
    const ids = new Set();
    equipes.forEach((eq) => { if (eq.carte) ids.add(eq.carte); });
    return ids;
  }, [equipes]);

  const cartesDisponibles = useMemo(
    () => cartes.filter((c) => !cartesDejaAttribuees.has(c.id)),
    [cartes, cartesDejaAttribuees]
  );

  // Adaptation automatique pour les cartes (utils/adaptationCartes.js) : les
  // remplaçants ANNULÉS sont retirés de la liste à lancer et l'origine reste
  // de côté ; le bloc « Adapté pour les cartes » garde la trace des deux.
  const [remplacementsAnnules, setRemplacementsAnnules] = useState(() => new Set());
  const annulerRemplacement = useCallback((remplacant) => {
    const cle = keyOf(remplacant);
    setRemplacementsAnnules((prev) => new Set(prev).add(cle));
    setOrderedQuestions((prev) => prev.filter((q) => keyOf(q) !== cle));
  }, []);

  const handleMove = useCallback((index, direction) => {
    setOrderedQuestions((prev) => {
      const next = [...prev];
      const target = index + direction;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  // ─── Partition ArUco — dérivée au render, une seule source de vérité ───
  // (orderedQuestions). compatiblesAruco filtre le lancement cartes et pilote
  // l'état du segment ; exclusAruco alimente le warning + le grisage de la liste.
  const compatiblesAruco = useMemo(() => orderedQuestions.filter(estQcmSimple), [orderedQuestions]);
  const exclusAruco = useMemo(() => orderedQuestions.filter((q) => !estQcmSimple(q)), [orderedQuestions]);
  const questionsJouables = isAruco ? compatiblesAruco : orderedQuestions;

  // En groupes, `exercices` d'un sujet = ses propres + le panier ; la séance
  // part avec ses propres + la liste de la modale (retraits compris).
  const nbExercicesSujet = (s) => s.exercices.length - selectedQuestions.length + orderedQuestions.length;
  // En groupes, au moins un sujet, et chacun avec au moins un exercice.
  const canLaunch = (sujets ? sujets.length > 0 && sujets.every((s) => nbExercicesSujet(s) > 0) : questionsJouables.length > 0)
    // En ligne le mode équipe ne s'applique pas (forcé individuel au launch) —
    // sans ce court-circuit, un mode 'equipe' choisi en cartes puis un switch
    // vers en ligne bloquerait le bouton sans cause visible.
    && (isEnLigne || mode !== 'equipe' || equipes.length > 0)
    && (launchMode !== 'schedule' || datePassation)
    && (launchMode !== 'now' || isEnLigne || allElevesAssigned) // En ligne : pas besoin de cartes
    && (!withTitre || titreSession.trim().length > 0); // Bilan : titre requis (serializer)

  const handleLaunch = () => {
    if (!canLaunch || isLoading) return;
    onLaunch({
      mode: isEnLigne ? 'individuel' : mode,
      mode_passation: format,
      dureeQuiz: isEnLigne ? dureeQuiz : null,
      // Objets complets (et pas des ids) : les exercices générés n'ont pas
      // encore d'id — c'est handleLaunch côté dashboard qui les persiste
      // (bulk-create) puis résout la liste d'ids dans l'ordre affiché ici.
      // En cartes, seuls les QCM simples partent (les exclus restent visibles
      // dans le modal mais ne sont jamais envoyés ni persistés).
      questions: questionsJouables,
      equipes: mode === 'equipe' && isAruco ? equipes : [],
      datePassation: launchMode === 'schedule' ? datePassation : null,
      launchNow: launchMode === 'now',
      correctionVisible,
      ...(caseCoupsDePouce ? { coupsDePouceAutorises: coupsDePouce } : {}),
      // « Donner aussi à » : une séance de plus par classe cochée (dashboard).
      classesSupplementaires: autresClasses.filter((c) => classesSupp.has(c.id)),
      nombresDifferents,
      // Déroulé de fin projetée — les deux clés ne partent qu'en cartes : hors
      // de ce format elles n'ont pas d'écran où s'appliquer, et le modèle garde
      // ses défauts.
      ...(isAruco ? { podiumActif, modeCorrection } : {}),
      // Présent uniquement quand le parent l'a demandé : bilans EvalNat (titre
      // requis) et séances QCM (un titre vidé reprend le titre automatique).
      ...(withTitre || titreAuto ? { titre: titreSession.trim() || titreAutomatique } : {}),
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
      <div className="w-full max-w-lg max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            {/* Même couleur que le bouton de la barre de lancement qui a
                ouvert la modale (indigo cartes, turquoise en ligne, ambre papier). */}
            {isPapier
              ? <Printer size={20} className="text-amber-500 flex-shrink-0" />
              : isEnLigne
                ? <Monitor size={20} className="text-teal-600 flex-shrink-0" />
                : <ScanLine size={20} className="text-indigo-500 flex-shrink-0" />
            }
            <div>
              <h3 className="text-base font-bold text-gray-800">
                {isPapier ? 'Générer l\'éval papier' : isEnLigne ? 'Faire en ligne' : 'Lancer avec les cartes'}
              </h3>
              <p className="text-xs text-gray-400">{orderedQuestions.length} question{orderedQuestions.length > 1 ? 's' : ''} sélectionnée{orderedQuestions.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 flex-1 overflow-y-auto">
          {motifCartes && (
            <p className="flex items-center gap-2 text-xs text-gray-500">
              <ScanLine size={14} className="text-gray-400 flex-shrink-0" />
              {motifCartes}
            </p>
          )}
          {isPapier ? (
            <div className="space-y-3.5">
              <div className="flex items-center gap-2 pb-1">
                <FileDown size={18} className="text-blue-600" />
                <h4 className="text-sm font-bold text-gray-800">
                  Générer l'évaluation en PDF
                </h4>
              </div>

              {/* Plus de choix « Évaluation / Entraînement » (2026-09-03) : un
                  seul document par défaut, l'évaluation (en-tête nom, classe,
                  date). documentType reste dans printMeta pour le pipeline. */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                  Titre de l'évaluation
                </label>
                <input
                  type="text"
                  value={printMeta.titre}
                  onChange={(e) => setPrintMeta((m) => ({ ...m, titre: e.target.value }))}
                  placeholder="Ex. Contrôle chapitre 11"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 focus:ring-2 focus:ring-blue-200/50 focus:border-blue-300 focus:outline-none"
                />
              </div>

              {/* Classe : auto-remplie depuis le contexte (classeNom/groupNom) —
                  plus de saisie manuelle. */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Date</label>
                <input
                  type="date"
                  value={printMeta.date}
                  onChange={(e) => setPrintMeta((m) => ({ ...m, date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 focus:ring-2 focus:ring-blue-200/50 focus:border-blue-300 focus:outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                  L'élève répond…
                </label>
                <div className="flex gap-2">
                  {[
                    { k: 'sur_la_feuille', l: 'Sur la feuille', d: 'Cases, lignes et tableaux imprimés' },
                    { k: 'sur_le_cahier', l: 'Sur le cahier', d: 'Énoncés seuls, consignes adaptées' },
                  ].map(({ k, l, d }) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setPrintMeta((m) => ({ ...m, saisieMode: k }))}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition text-left ${
                        printMeta.saisieMode === k
                          ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                          : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <div className="font-semibold">{l}</div>
                      <div className="text-[10px] opacity-70 mt-0.5">{d}</div>
                    </button>
                  ))}
                </div>
                {printMeta.saisieMode === 'sur_le_cahier' && (
                  <p className="mt-1.5 text-[10px] text-gray-400">
                    Les exercices de construction (droite graduée, compas) restent à compléter sur la feuille.
                  </p>
                )}
              </div>

              {sujets ? (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Un sujet par groupe</p>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {sujets.map((s) => (
                      <li key={s.numero}>
                        <strong>{s.titre}</strong> · {s.eleves.length} élève{s.eleves.length > 1 ? 's' : ''} · {s.exercices.length} exercice{s.exercices.length > 1 ? 's' : ''}
                        {avecPlanche(s) ? ' · avec la planche de coups de pouce' : ''}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-[11px] text-gray-500">
                    Un seul PDF : le récapitulatif réservé au professeur, les sujets à photocopier, puis les corrigés. Aucun nom de groupe sur les copies.
                  </p>
                </div>
              ) : (<>
              {/* Planche de coups de pouce à découper. Les cartes partent sur
                  des feuilles séparées du sujet : une aide posée à côté de son
                  exercice serait lue avant l'essai, donc ce ne serait plus une
                  aide. Toutes les fiches ne s'impriment pas (figures animées à
                  l'écran) : le compte annoncé est celui de la planche réelle. */}
              <label
                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border transition ${
                  cartesAide.length > 0
                    ? 'border-amber-200 bg-amber-50/60 cursor-pointer hover:bg-amber-50'
                    : 'border-gray-200 bg-gray-50 opacity-60'
                }`}
              >
                <input
                  type="checkbox"
                  disabled={cartesAide.length === 0}
                  checked={printMeta.coupsDePouce}
                  onChange={(e) => setPrintMeta((m) => ({ ...m, coupsDePouce: e.target.checked }))}
                  className="mt-0.5 accent-amber-600"
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                    <Scissors size={14} className="text-amber-600" />
                    Planche de coups de pouce à découper
                  </span>
                  <span className="block text-[11px] text-gray-500 mt-0.5">
                    {cartesAide.length > 0 ? (
                      <>
                        {nbExosAides} exercice{nbExosAides > 1 ? 's' : ''} sur {orderedQuestions.length} {nbExosAides > 1 ? 'ont' : 'a'} une
                        aide imprimable, soit {cartesAide.length} carte{cartesAide.length > 1 ? 's' : ''}.
                        Elles partent sur des feuilles séparées, à la fin : une seule impression suffit.
                        À donner à l'élève qui bloque, pendant un entraînement ou une remédiation.
                        Ces aides utilisent la couleur, imprime-les en couleur si tu peux.
                      </>
                    ) : (
                      "Aucune aide imprimable pour ces exercices."
                    )}
                  </span>
                </span>
              </label>
              </>)}

              {/* Plus d'encart « depuis l'aperçu tu pourras créer le suivi »
                  (2026-09-03) : l'aperçu le propose lui-même. */}

            </div>
          ) : null}

          {/* Adapté pour les cartes : ce qui a été fait AUTOMATIQUEMENT à
              l'ouverture (remplacements par un QCM, mises de côté), avec
              aperçu et annulation par ligne — jamais une question au prof
              (décision 2026-09-02). */}
          {isAruco && adaptation && (adaptation.remplacements.length > 0 || adaptation.laisses.length > 0) && (
            <div className="rounded-xl bg-blue-50 border border-blue-100 px-3.5 py-2.5">
              {/* Titre, puis un chiffre en gras par ligne ; « voir le détail »
                  pour la liste (allègement 2026-09-03). */}
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-blue-600 flex-shrink-0" />
                <span className="flex-1 text-xs font-semibold text-blue-800">Adaptation pour les cartes</span>
                <button type="button" onClick={() => setAdaptationDepliee((v) => !v)} className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex-shrink-0">
                  {adaptationDepliee ? 'masquer' : 'voir le détail'}
                </button>
              </div>
              <ul className="mt-1 ml-6 space-y-0.5 text-xs text-blue-700">
                {adaptation.remplacements.length > 0 && (
                  <li><strong className="text-sm">{adaptation.remplacements.length}</strong> converti{adaptation.remplacements.length > 1 ? 's' : ''} en QCM</li>
                )}
                {adaptation.laisses.length > 0 && (
                  <li><strong className="text-sm">{adaptation.laisses.length}</strong> non compatible{adaptation.laisses.length > 1 ? 's' : ''}, laissé{adaptation.laisses.length > 1 ? 's' : ''} de côté</li>
                )}
              </ul>
              {adaptationDepliee && (
              <div className="space-y-1.5 ml-1 mt-2">
                {adaptation.remplacements.map(({ origine, remplacant }) => {
                  const annule = remplacementsAnnules.has(keyOf(remplacant));
                  return (
                    <div key={keyOf(remplacant)} className={`flex items-center gap-2 text-[11px] ${annule ? 'opacity-60' : ''}`}>
                      <span className="text-[9px] font-medium text-purple-600 bg-purple-50 px-1 py-0.5 rounded flex-shrink-0">{getQuestionTypeLabel(origine)}</span>
                      <span className="text-gray-400 flex-shrink-0">→</span>
                      <span className="text-[9px] font-medium text-blue-700 bg-blue-50 px-1 py-0.5 rounded flex-shrink-0">QCM</span>
                      <span className="flex-1 text-gray-600 truncate">{(origine.texte || '').replace(/<[^>]*>/g, '').slice(0, 50)}</span>
                      {annule ? (
                        <span className="text-gray-500 flex-shrink-0">laissé de côté</span>
                      ) : (
                        <>
                          <button type="button" onClick={() => setPreviewQuestion(remplacant)} className="p-0.5 rounded text-gray-400 hover:text-blue-600 transition" title="Voir le QCM qui partira">
                            <Eye size={13} />
                          </button>
                          <button type="button" onClick={() => annulerRemplacement(remplacant)} className="text-blue-600 hover:text-blue-800 font-semibold flex-shrink-0">
                            annuler
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
                {adaptation.laisses.map((q) => (
                  <div key={keyOf(q)} className="flex items-center gap-2 text-[11px] text-gray-500">
                    <span className="text-[9px] font-medium text-purple-600 bg-purple-50 px-1 py-0.5 rounded flex-shrink-0">{getQuestionTypeLabel(q)}</span>
                    <span className="flex-1 truncate">{(q.texte || '').replace(/<[^>]*>/g, '').slice(0, 50)}</span>
                    <span className="flex-shrink-0">laissé de côté, reste dans le panier</span>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}

          {/* Warning questions exclues (ArUco — non-QCM), quand aucune
              adaptation n'a été faite (format basculé dans la modale). */}
          {isAruco && !adaptation && exclusAruco.length > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
                <span className="text-xs font-semibold text-amber-700">
                  {exclusAruco.length} question{exclusAruco.length > 1 ? 's' : ''} exclue{exclusAruco.length > 1 ? 's' : ''} — seuls les QCM à choix unique sont autorisés en mode ArUco
                </span>
              </div>
              <div className="space-y-1 ml-5">
                {exclusAruco.map(q => (
                  <div key={keyOf(q)} className="flex items-center gap-1.5 text-[11px]">
                    <X size={11} className="text-red-400 flex-shrink-0" />
                    <span className="text-gray-500 truncate">{(q.texte || '').replace(/<[^>]*>/g, '').slice(0, 50)}</span>
                    <span className="text-[9px] font-medium text-purple-600 bg-purple-50 px-1 py-0.5 rounded flex-shrink-0">
                      {getQuestionTypeLabel(q)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isPapier && (<>
          {/* F7 en ligne : une séance par groupe non vide, sous le même code
              neutre que sur papier, avec le réglage de coups de pouce du groupe. */}
          {isEnLigne && sujets && (
            <div>
              <p id="passations-groupes" className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Une passation par groupe</p>
              <ul aria-labelledby="passations-groupes" className="space-y-1 text-sm text-gray-700">
                {sujets.map((s) => (
                  <li key={s.numero}>
                    <strong>{s.titre}</strong> · {pluriel(s.eleves.length, 'élève')} · {pluriel(nbExercicesSujet(s), 'exercice')} · {s.plancheAides ? 'coups de pouce autorisés' : 'sans coup de pouce'}
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-[11px] text-gray-500">
                Chaque élève ne voit que le sujet de son groupe. Durée et correction valent pour toutes.
              </p>
            </div>
          )}
          {/* Titre — bilans EvalNat uniquement (withTitre) */}
          {withTitre && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Titre</label>
              <input
                type="text"
                value={titreSession}
                onChange={(e) => setTitreSession(e.target.value)}
                placeholder="Ex. Bilan dixièmes — semaine 12"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 focus:ring-2 focus:ring-emerald-200/50 focus:border-emerald-300 focus:outline-none"
              />
            </div>
          )}

          {/* « Donner aussi à » : les autres classes du même niveau, mêmes
              questions et mêmes réglages, une séance par classe cochée. */}
          {autresClasses.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">{motsCibles.titre}</label>
              <div className="flex flex-wrap gap-2">
                {autresClasses.map((c) => {
                  const coche = classesSupp.has(c.id);
                  const etat = avancementClasses?.find((a) => a.id === c.id)?.etat;
                  return (
                    <label
                      key={c.id}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium cursor-pointer transition ${
                        coche ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <input type="checkbox" checked={coche} onChange={() => basculerClasse(c.id)} className="accent-blue-600" disabled={isLoading} />
                      {c.nom}
                      {etat === 'ok' && <span className="text-emerald-600" aria-label="créée">✓</span>}
                      {etat === 'en_cours' && <span className="text-gray-400" aria-label="en cours">…</span>}
                      {etat === 'echec' && <span className="text-red-500" aria-label="échec">✗</span>}
                    </label>
                  );
                })}
              </div>
              {classesSupp.size > 0 && (
                <div className="mt-2 space-y-1">
                  <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={nombresDifferents} onChange={(e) => setNombresDifferents(e.target.checked)} className="accent-blue-600" disabled={isLoading} />
                    Nombres différents pour {motsCibles.chaque} (les exercices générés sont retirés au sort)
                  </label>
                  {!isEnLigne && mode === 'equipe' && (
                    <p className="text-[11px] text-gray-400">{motsCibles.equipes}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Réglages repliés : les valeurs courantes en pastilles. */}
          <Pli
            titre="Réglages"
            icone={<SlidersHorizontal size={15} />}
            badges={isEnLigne
              ? [dureeQuiz >= 60 ? `${dureeQuiz / 60}h` : `${dureeQuiz} min`, `Correction ${correctionVisible ? 'visible' : 'masquée'}`,
                ...(caseCoupsDePouce ? [coupsDePouce ? 'Coups de pouce' : 'Sans coup de pouce'] : [])]
              : [mode === 'equipe' ? 'Par équipes' : 'Individuel', podiumActif ? 'Podium' : 'Sans podium', `Correction ${modeCorrection === 'finale' ? 'en fin de séance' : 'après chaque question'}`]}
            ouvert={reglagesOuverts}
            onToggle={() => setReglagesOuverts((v) => !v)}
          >
          {/* Titre de la séance : le titre automatique, à changer si on veut. */}
          {titreAuto ? (
            <div>
              <label htmlFor="titre-seance" className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Titre</label>
              <input
                id="titre-seance"
                type="text"
                value={titreSession}
                onChange={(e) => setTitreSession(e.target.value)}
                placeholder={titreAutomatique}
                maxLength={200}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 focus:ring-2 focus:ring-blue-300/50 focus:border-blue-300 focus:outline-none"
              />
            </div>
          ) : null}

          {/* Mode — uniquement en classe (ArUco supporte les équipes) */}
          {!isEnLigne && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Mode</label>
              <div className="flex gap-2">
                {['individuel', 'equipe'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                      mode === m
                        ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {m === 'individuel' ? 'Individuel' : 'Équipe'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Durée globale du quiz — mode en ligne uniquement */}
          {isEnLigne && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                <Clock size={12} className="inline mr-1" />
                Durée
              </label>
              <div className="flex flex-wrap gap-1.5">
                {[15, 30, 45, 60].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setDureeQuiz(val)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      dureeQuiz === val
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {val >= 60 ? `${val / 60}h` : `${val} min`}
                  </button>
                ))}
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={5}
                    max={180}
                    value={dureeQuiz}
                    onChange={(e) => setDureeQuiz(parseInt(e.target.value, 10) || 30)}
                    className="w-16 px-2 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 text-center focus:ring-2 focus:ring-emerald-200/50 focus:border-emerald-300 focus:outline-none"
                  />
                  <span className="text-xs text-gray-400">min</span>
                </div>
              </div>
            </div>
          )}

          {/* Correction visible — mode en ligne uniquement */}
          {isEnLigne && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Correction</label>
              <button
                type="button"
                onClick={() => setCorrectionVisible(prev => !prev)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                  correctionVisible
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {correctionVisible ? <Eye size={15} /> : <EyeOff size={15} />}
                {correctionVisible ? 'Correction visible après passation' : 'Correction masquée'}
                <div className={`relative w-9 h-5 rounded-full transition-colors ml-2 ${correctionVisible ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${correctionVisible ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
              </button>
            </div>
          )}

          {caseCoupsDePouce && (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={coupsDePouce} onChange={(e) => setCoupsDePouce(e.target.checked)} className="accent-emerald-600" disabled={isLoading} />
              Coups de pouce autorisés
            </label>
          )}

          {/* Déroulé de fin de séance — passation cartes uniquement.
              Strictement symétrique du bloc « Correction » gardé par isEnLigne
              juste au-dessus : les deux ne s'affichent jamais ensemble. Le
              réglage d'ici dit QUAND la bonne réponse s'affiche sur l'écran de
              la classe ; `correction_visible` dit si l'élève peut la consulter
              après coup depuis son espace. Deux axes distincts (D6). */}
          {isAruco && (
            <>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Podium</label>
                <button
                  type="button"
                  aria-pressed={podiumActif}
                  onClick={() => setPodiumActif((prev) => !prev)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    podiumActif
                      ? 'bg-blue-50 text-blue-600'
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  <Trophy size={15} />
                  {podiumActif ? 'Podium projeté en fin de séance' : 'Pas de podium'}
                  <div className={`relative w-9 h-5 rounded-full transition-colors ml-2 ${podiumActif ? 'bg-blue-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${podiumActif ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </button>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Correction</label>
                <div className="flex gap-2">
                  {[
                    { k: 'finale', l: 'En fin de séance', d: 'Toutes les questions, puis la correction projetée' },
                    { k: 'consecutive', l: 'Après chaque question', d: 'La bonne réponse s\'affiche aussitôt' },
                  ].map(({ k, l, d }) => (
                    <button
                      key={k}
                      type="button"
                      aria-pressed={modeCorrection === k}
                      onClick={() => setModeCorrection(k)}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition text-left ${
                        modeCorrection === k
                          ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                          : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <div className="font-semibold">{l}</div>
                      <div className="text-[10px] opacity-70 mt-0.5">{d}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          </Pli>

          {/* Lancer maintenant ou programmer — AVANT la liste des questions
              (déplacé, demande prof 2026-09-01) : « Quand » est court et
              décisif, « Ordre » peut faire 15 lignes ; enterré dessous, il
              fallait scroller pour découvrir « Maintenant / Programmer ». */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Quand</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setLaunchMode('now')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border-2 transition ${
                  launchMode === 'now'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-500'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Play size={14} />
                Maintenant
              </button>
              <button
                type="button"
                onClick={() => setLaunchMode('schedule')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border-2 transition ${
                  launchMode === 'schedule'
                    ? 'bg-amber-50 text-amber-700 border-amber-500'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Calendar size={14} />
                Programmer
              </button>
            </div>
            {launchMode === 'now' && !isEnLigne && !allElevesAssigned && (
              <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Certains élèves n'ont pas de carte QCM assignée.{' '}
                {onGoToCartes ? (
                  <button onClick={onGoToCartes} className="underline hover:text-amber-700 font-medium">
                    Assigner les cartes
                  </button>
                ) : (
                  'Assignez les cartes'
                )}
                {' '}ou programmez la session pour plus tard.
              </p>
            )}
            {launchMode === 'schedule' && (
              <input
                type="date"
                value={datePassation}
                onChange={(e) => setDatePassation(e.target.value)}
                className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 focus:ring-2 focus:ring-amber-200/50 focus:border-amber-300 focus:outline-none"
              />
            )}
          </div>

          {/* Ordre des questions — replié, le compte (et les hors cartes) en pastilles */}
          <Pli
            titre="Ordre des questions"
            icone={<ListOrdered size={15} />}
            badges={(() => {
              const nbHors = isAruco ? orderedQuestions.filter((q) => !estQcmSimple(q)).length : 0;
              return [
                `${orderedQuestions.length} question${orderedQuestions.length > 1 ? 's' : ''}`,
                ...(nbHors > 0 ? [`${nbHors} hors cartes`] : []),
              ];
            })()}
            ouvert={ordreOuvert}
            onToggle={() => setOrdreOuvert((v) => !v)}
          >
            <ol className="space-y-1">
              {orderedQuestions.map((q, i) => {
                const exclue = isAruco && !estQcmSimple(q);
                return (
                <li key={keyOf(q)} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${exclue ? 'bg-gray-50/50 border-gray-100 opacity-50' : 'bg-gray-50 border-gray-100'}`}>
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex-shrink-0">
                    {i + 1}
                  </span>
                  <span
                    className="flex-1 text-xs text-gray-600 truncate [&_table]:hidden [&_img]:hidden"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml((q.texte || '').replace(/\{\{element:\d+\}\}/g, '')) }}
                  />
                  {exclue ? (
                    <span className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-semibold flex-shrink-0" title="Non compatible cartes ArUco — ne sera pas envoyée en mode cartes">
                      exclue
                    </span>
                  ) : null}
                  {/* Badge source pour les items sans id DB : EvalNat (jetable,
                      jamais persisté) vs TanoEd (persisté au lancement). */}
                  {!q.id ? (
                    q._meta?.source === 'evalnat' ? (
                      <span className="px-1.5 py-0.5 rounded-md bg-violet-100 text-violet-700 text-[9px] font-semibold flex-shrink-0" title="Question de la banque EvalNat">
                        EvalNat
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded-md bg-blue-100 text-blue-700 text-[9px] font-semibold flex-shrink-0" title="Exercice généré — sera ajouté à votre banque au lancement">
                        TanoEd
                      </span>
                    )
                  ) : null}
                  <div className="flex gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => setPreviewQuestion(q)}
                      className="p-0.5 rounded text-gray-300 hover:text-blue-500 transition"
                      title="Aperçu"
                    >
                      <Eye size={13} />
                    </button>
                    <button
                      onClick={() => handleMove(i, -1)}
                      disabled={i === 0}
                      className="p-0.5 rounded text-gray-400 hover:text-gray-700 disabled:opacity-30 transition"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      onClick={() => handleMove(i, 1)}
                      disabled={i === orderedQuestions.length - 1}
                      className="p-0.5 rounded text-gray-400 hover:text-gray-700 disabled:opacity-30 transition"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                </li>
                );
              })}
            </ol>
          </Pli>

          {/* Équipes (mode equipe, ArUco uniquement) */}
          {!isEnLigne && mode === 'equipe' && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                <Users size={12} className="inline mr-1" />
                Équipes
              </label>

              {equipes.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {equipes.map((eq, i) => (
                    <div key={i} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
                      <div>
                        <span className="text-xs font-semibold text-gray-700">{eq.nom}</span>
                        <span className="text-[10px] text-gray-400 ml-2">{eq.eleves.length} élève{eq.eleves.length > 1 ? 's' : ''}</span>
                      </div>
                      <button
                        onClick={() => setEquipes((prev) => prev.filter((_, idx) => idx !== i))}
                        className="text-[10px] text-red-400 hover:text-red-600 transition"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!showEquipeForm && elevesDisponibles.length > 0 && (
                <button
                  onClick={() => setShowEquipeForm(true)}
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium transition"
                >
                  <Plus size={14} />
                  Ajouter une équipe
                </button>
              )}

              {showEquipeForm && elevesDisponibles.length > 0 && cartesDisponibles.length > 0 && (
                <EquipeForm
                  cartes={cartesDisponibles}
                  eleves={elevesDisponibles}
                  onSave={(data) => { setEquipes((prev) => [...prev, data]); setShowEquipeForm(false); }}
                  onCancel={() => { if (equipes.length > 0) setShowEquipeForm(false); }}
                />
              )}

              {elevesDisponibles.length === 0 && equipes.length > 0 && (
                <p className="text-[10px] text-emerald-600 text-center py-1">Tous les élèves sont répartis</p>
              )}
            </div>
          )}

          {/* Erreur */}
          {error && (
            <div role="alert" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs">
              {error}
            </div>
          )}
          </>)}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition"
            >
              Annuler
            </button>
            {isPapier && sujets ? (
              <button
                type="button"
                onClick={ouvrirGroupes}
                disabled={!printMeta.titre.trim() || sujets.length === 0}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-500/20 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none transition"
              >
                <FileDown size={16} />
                Générer le PDF
              </button>
            ) : isPapier ? (() => {
              // Type évaluation : le titre est REQUIS (serializer backend) — le
              // panneau de l'aperçu créera l'évaluation avec ce titre.
              const titreManquant = !printMeta.titre.trim();
              const peutOuvrir = orderedQuestions.length > 0 && !titreManquant;
              return (
                <button
                  type="button"
                  onClick={handleOpenPrint}
                  disabled={!peutOuvrir}
                  title={titreManquant ? "Donne un titre à l'évaluation (requis pour le suivi)" : undefined}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                    peutOuvrir
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-500/20'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <FileDown size={16} />
                  Ouvrir l'aperçu PDF
                </button>
              );
            })() : (
              <button
                onClick={handleLaunch}
                disabled={!canLaunch || isLoading}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  canLaunch && !isLoading
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isEnLigne ? <Monitor size={16} /> : <ScanLine size={16} />}
                {isLoading ? 'Lancement...' : launchMode === 'now' ? 'Lancer la séance' : 'Programmer la séance'}
              </button>
            )}
          </div>
        </div>
      </div>

      {previewQuestion && (
        <QuestionPreviewModal
          question={previewQuestion}
          onClose={() => setPreviewQuestion(null)}
        />
      )}
    </div>,
    document.body
  );
};

export default LaunchModal;
